import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Location, NgClass } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseFormComponent } from 'components/form/base-form';
import { ApiService } from 'services/api.service';
import { AuthorizationService } from 'services/authorization.service';
import { ToastService } from 'services/toast.service';
import { Router } from '@angular/router';
import { ReadingRoomListItem } from 'types/reading-room.interface';
import { TimeInputComponent } from 'components/time-input/time-input.component';
import { DatePickerComponent, DateRangeMode } from 'components/date-picker/date-picker.component';
import { getControlsWithErrors, MAX_LENGTH_NAME, timeFormatValidator, timeRangeValidator } from 'util/form.utility';
import { ServiceFormActionsComponent } from 'components/service/service-form-actions/service-form-actions.component';
import {
  BackofficeReadingRoomSubmissionCard,
  InitializeBackofficeReadingRoomSubmissionRequest,
  UpdateDraftReadingRoomSubmissionRequest
} from 'types/service.interface';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { ClientCard } from 'types/client.interface';

@Component({
  selector: 'app-visit-info-form',
  standalone: true,
  imports: [
    TimeInputComponent,
    DatePickerComponent,
    ReactiveFormsModule,
    TranslateModule,
    NgClass,
    ServiceFormActionsComponent,
    TooltipComponent,
    ConfirmModalComponent
  ],
  templateUrl: './visit-info-form.component.html',
  styleUrls: ['../../../form/base-form.scss', './visit-info-form.component.scss']
})
export class VisitInfoFormComponent extends BaseFormComponent implements OnInit {
  public readonly maxFieldLength = MAX_LENGTH_NAME;
  @Input() readingRooms: ReadingRoomListItem[] = [];
  @Input() client!: ClientCard;
  @Input() readersCardId!: string;
  @Input() application?: BackofficeReadingRoomSubmissionCard;
  @Input() initialReadingRoomId?: number;
  @Output() applicationUpdated: EventEmitter<BackofficeReadingRoomSubmissionCard> = new EventEmitter<BackofficeReadingRoomSubmissionCard>();
  @Output() saveDraft: EventEmitter<UpdateDraftReadingRoomSubmissionRequest> = new EventEmitter<UpdateDraftReadingRoomSubmissionRequest>();
  @Output() formChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  public readonly dateRangeMode = DateRangeMode;

  isOpenConfirmDeleteSubmission = false;
  isLoading: boolean = false;
  readingRoomOptions: { label?: string; value?: string }[] = [];

  get visitDate(): AbstractControl<any, any> | null {
    return this.form.get('visitDate');
  }
  get visitTimeFrom(): AbstractControl<any, any> | null {
    return this.form.get('visitTimeFrom');
  }
  get visitTimeTo(): AbstractControl<any, any> | null {
    return this.form.get('visitTimeTo');
  }
  get visitInformation(): AbstractControl<any, any> | null {
    return this.form.get('visitInformation');
  }
  get agrrementToReadingRoomTerms(): AbstractControl<any, any> | null {
    return this.form.get('agreementToReadingRoomTerms');
  }

  constructor(
    protected override location: Location,
    protected override router: Router,
    protected override formBuilder: FormBuilder,
    protected override apiService: ApiService,
    protected override toastService: ToastService,
    protected authService: AuthorizationService,
    protected override translateService: TranslateService
  ) {
    super(location, router, formBuilder, apiService, toastService, translateService);
  }

  override ngOnInit(): void {
    this.readingRoomOptions = this.readingRooms.map((room: ReadingRoomListItem) => ({
      label: room?.name ?? '',
      value: room?.id ?? ''
    }));
    this.fillForm();

    this.visitDate?.valueChanges.subscribe(() => {
      this.form.get('visitTimeFrom')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      this.form.get('visitTimeTo')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });

    this.visitTimeFrom?.valueChanges.subscribe(() => {
      this.form.get('visitTimeTo')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });

    setTimeout(() => {
      this.form.updateValueAndValidity();
    });

    this.form.markAsPristine();
    this.formChanged.emit(false);
    this.form.valueChanges.subscribe(() => {
      this.formChanged.emit(this.form.dirty);
    });
  }

  override fillForm(): void {
    this.form = this.formBuilder.group({
      readingRoomId: [this.application?.readingRoom?.id || this.initialReadingRoomId || null, [Validators.required]],
      visitDate: [this.application?.visitDate || '', Validators.required],
      visitTimeFrom: [this.application?.visitTimeFrom || '', [Validators.required, timeFormatValidator()]],
      visitTimeTo: [this.application?.visitTimeTo || '', [timeRangeValidator('visitTimeFrom'), timeFormatValidator()]],
      visitInformation: [this.application?.visitInformation || '', [Validators.maxLength(this.maxFieldLength)]],
      agreementToReadingRoomTerms: [this.application?.agreementToReadingRoomTerms || false]
    });
  }

  override submit(): void {
    this.submitAttempted = true;

    if (!this.readersCardId) {
      this.toastService.showDanger('readersCardRequired', '', {
        shouldTranslate: true
      });
      return;
    }

    if (this.form.valid) {
      this.isLoading = true;

      if (!this.application) {
        const args: InitializeBackofficeReadingRoomSubmissionRequest = {
          ...this.form.value,
          clientId: this.client.id,
          readerCardId: this.readersCardId,
          visitTimeFrom: this.visitTimeFrom?.value || null,
          visitTimeTo: this.visitTimeTo?.value || null
        };

        this.apiService.initVisitRoomApplication(args).subscribe(
          (data: BackofficeReadingRoomSubmissionCard) => {
            this.toastService.showSuccess('draftSaved', '', {
              shouldTranslate: true
            });
            this.applicationUpdated.emit(data);
            this.isLoading = false;
            this.router.navigate([`/pakalpojumi/lasitavas-apmeklejums/${data.id}`], {
              queryParams: { solis: 2 },
              queryParamsHandling: 'merge'
            });
          },
          (error: any) => {
            this.isLoading = false;
            console.error(error);
            if (
              error.error.errors['readerCardId']?.[0] === 'The field ReaderCardId must be between 1 and 2147483647.' ||
              error.error.errors['ReaderCard']?.[0] === 'Client does not have reader card'
            ) {
              this.toastService.showDanger('noReadersCard', '', {
                shouldTranslate: true
              });
            } else {
              this.toastService.showRequestError();
            }
          }
        );
      } else {
        const args: UpdateDraftReadingRoomSubmissionRequest = {
          readingRoomSubmissionId: this.application.id,
          ...this.form.value,
          clientId: this.client.id,
          readerCardId: this.readersCardId,
          visitTimeFrom: this.visitTimeFrom?.value || null,
          visitTimeTo: this.visitTimeTo?.value || null,
          researchTopicIds: this.getResearchTopicsId(),
          requestedDocumentsInformation: this.application.requestedDocumentsInformation,
          hasRequestedDocuments: this.application?.hasRequestedDocuments,
          requestedDocuments: this.application ? this.application.requestedDocuments || [] : []
        };

        this.apiService.updateVisitRoomInfoDraft(args).subscribe(
          (data: BackofficeReadingRoomSubmissionCard) => {
            this.isLoading = false;
            this.toastService.showSuccess('draftSaved', '', {
              shouldTranslate: true
            });
            this.applicationUpdated.emit(data);
            this.router.navigate([`/pakalpojumi/lasitavas-apmeklejums/${data.id}`], {
              queryParams: { solis: 2 },
              queryParamsHandling: 'merge'
            });
          },
          (error: any) => {
            this.isLoading = false;
            console.error(error);
            if (
              error.error.errors['readerCardId']?.[0] === 'The field ReaderCardId must be between 1 and 2147483647.' ||
              error.error.errors['ReaderCard']?.[0] === 'Client does not have reader card'
            ) {
              this.toastService.showDanger('noReadersCard', '', {
                shouldTranslate: true
              });
            } else {
              this.toastService.showRequestError();
            }
          }
        );
      }
    } else {
      this.isLoading = false;
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'pages.service.visitRoom');
    }
  }

  handleSaveDraft(): void {
    this.submitAttempted = true;

    if (!this.readersCardId) {
      this.toastService.showDanger('readersCardRequired', '', {
        shouldTranslate: true
      });
      return;
    }

    if (this.form.valid) {
      this.isLoading = true;

      if (this.application) {
        const args: UpdateDraftReadingRoomSubmissionRequest = {
          readingRoomSubmissionId: this.application.id,
          ...this.form.value,
          visitTimeFrom: this.visitTimeFrom?.value || null,
          visitTimeTo: this.visitTimeTo?.value || null,
          clientId: this.application.client?.id,
          readerCardId: this.application.readerCard?.id,
          readingRoomId: this.application.readingRoom?.id,
          researchTopicIds: this.getResearchTopicsId(),
          hasRequestedDocuments: this.application?.hasRequestedDocuments,
          requestedDocumentsInformation: this.application.requestedDocumentsInformation || '',
          requestedDocuments: this.application ? this.application.requestedDocuments || [] : []
        };

        this.saveDraft.emit(args);
        this.isLoading = false;
      } else {
        const args: InitializeBackofficeReadingRoomSubmissionRequest = {
          ...this.form.value,
          clientId: this.client.id,
          readerCardId: this.readersCardId,
          visitTimeFrom: this.visitTimeFrom?.value || null,
          visitTimeTo: this.visitTimeTo?.value || null
        };

        this.apiService.initVisitRoomApplication(args).subscribe(
          (data: BackofficeReadingRoomSubmissionCard) => {
            this.toastService.showSuccess('draftSaved', '', {
              shouldTranslate: true
            });
            this.router.navigate([`/pakalpojumi/lasitavas-apmeklejums/${data.id}`], {
              queryParams: { solis: 1 },
              queryParamsHandling: 'merge'
            });
            this.applicationUpdated.emit(data);
            this.isLoading = false;
            this.application = data;
          },
          (error: any) => {
            console.error(error);
            this.isLoading = false;
            if (error.error.errors['readerCardId'][0] === 'The field ReaderCardId must be between 1 and 2147483647.') {
              this.toastService.showDanger('noReadersCard', '', {
                shouldTranslate: true
              });
            } else {
              this.toastService.showRequestError();
            }
          }
        );
      }
    } else {
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'pages.service.visitRoom');

      this.submitAttempted = false;
      this.isLoading = false;
    }
  }

  getResearchTopicsId(): any[] {
    if (this.application) {
      return this.application.researchTopics?.map((topic) => topic.id) || [];
    }

    return [];
  }

  override back(confirmed: boolean = false): void {
    if (!confirmed) {
      this.isOpenConfirmBack = true;
    } else {
      this.formChanged.emit(false);
      this.router.navigate(['jauns-pakalpojuma-pieteikums']);
    }
  }

  onModalClose(): void {
    this.isOpenConfirmDeleteSubmission = false;
  }

  handleDeleteDraft() {
    this.isOpenConfirmDeleteSubmission = true;
  }

  deleteReadingRoomSubmission() {
    if (this.application?.id) {
      this.apiService.deleteReadingRoomSubmission(this.application?.id).subscribe(
        () => {
          this.router.navigate(['/lasitavas-apmeklejums']);
          this.toastService.showSuccess('applicationDraftDeleted', '', {
            shouldTranslate: true
          });
        },
        (error: any) => {
          console.error(error);
          this.toastService.showRequestError();
        }
      );
    }
  }
}
